import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { PiBuildingOfficeBold, PiStudent } from "react-icons/pi";
import { callApi, CDN_URL, formatDate, formatMoney, formatMoneyShorten, getInlineLoader, setCookie, getCookie } from "../Helpers";
import { FaChalkboardTeacher } from "react-icons/fa";
import { SiGoogleclassroom } from "react-icons/si";
import { IoIosPeople } from "react-icons/io";
import { MdSearch } from "react-icons/md";
import { FiCheckCircle, FiClock, FiArrowRight, FiEyeOff, FiFileText, FiMail } from "react-icons/fi";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);

  /* State Management */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  
  // States for Application Flow
  const [isApplying, setIsApplying] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");

  async function init() {
    setReady(false);
    await Promise.all([
      getData(),
      appContext.authCheck(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if(response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError('Could not fetch data');
      }
    });
  }

  /* --- APPLICATION FLOW LOGIC --- */
  async function handleApplyInitiation() {
    setIsApplying(true);
    
    // 1. Manually evaluate presence of localized security cookies
    const storedUuid = getCookie("userUuid");
    const storedToken = getCookie("userToken");

    // 2. If present locally, run the backend auth validation loop
    const isAuthenticated = appContext.auth;
    
    setIsApplying(false);

    if (isAuthenticated) {
      sendApplication();
    } else {
      // Fallback safety if local cookies are corrupted or dead on server
      setShowEmailPrompt(true);
      setShowOtpPrompt(false);
    }
  }

  async function handleEmailSubmit() {
    if (!emailInput || !emailInput.includes('@')) {
      appContext.tellError("Please enter a valid email address.");
      return;
    }

    setIsApplying(true);
    
    await callApi('jobapplicant/access-account', { email: emailInput }).then(response => {
      if (response.status === 1) {
        appContext.tellMessage(response.msg);
        setShowOtpPrompt(true); 
      } else {
        appContext.tellError(response.msg || "Failed to process request.");
      }
    }).catch(() => {
      appContext.tellError("Network error occurred.");
    });
    
    setIsApplying(false);
  }

  async function handleOtpSubmit() {
    if (!otpInput) {
      appContext.tellError("Please enter the OTP.");
      return;
    }

    setIsApplying(true);

    await callApi('jobapplicant/verify-otp', { email: emailInput, otp: otpInput }).then(response => {
      if (response.status === 1) {
        appContext.tellMessage(response.msg);
        setCookie('userUuid', response.data.uuid, 30);
        setCookie('userToken', response.data.token, 30);
        resetPrompts();
        sendApplication();
      } else {
        appContext.tellError(response.msg || "Invalid OTP.");
      }
    }).catch(() => {
      appContext.tellError("Network error occurred.");
    });

    setIsApplying(false);
  }

  function resetPrompts() {
    setShowEmailPrompt(false);
    setShowOtpPrompt(false);
    setEmailInput("");
    setOtpInput("");
  }

  async function sendApplication() {
    setIsApplying(true);
    try {
      const response = await callApi('jobapplicant/get-current-applicant-data', {});
      
      if (response && response.status === 1) {
        const applicant = response.data;
        setProfile(applicant); // Keep state updated for tracking references

        // Evaluate raw properties immediately to avoid state delay lag
        const isIncomplete = 
          !applicant.firstName?.trim() ||
          !applicant.middleName?.trim() ||
          !applicant.lastName?.trim() ||
          !applicant.phone?.trim() ||
          !applicant.physicalAddress?.trim() ||
          !applicant.dob?.trim() ||
          !applicant.gender?.trim();

        if (isIncomplete) {
          appContext.tellError("Please complete your profile details before submitting an application.");
          appContext.navTo({
            item: 'side-view',
            subItem: 'edit-applicant-profile'
          });
        } else {
          appContext.navTo({
            item: 'side-view',
            subItem: 'create-application', 
            extraItem: data.uuid
          });
        }
      } else {
        appContext.tellError(response.msg || "Could not retrieve verification profile.");
      }
    } catch (err) {
      appContext.tellError("Network connection error verification loop.");
    } finally {
      setIsApplying(false);
    }
  }

  async function publish() {
    setLoading(true);
    await callApi('publish-news', { uuid }).then(response => {
      if(response.status === 1) {
        appContext.tellMessage("Successful");
        getData();
      } else {
        appContext.tellError(response.msg);
      }
    });
    setLoading(false);
  }

  async function unpublish() {
    setLoading(true);
    await callApi('unpublish-news', { uuid }).then(response => {
      if(response.status === 1) {
        appContext.tellMessage("Successful");
        getData();
      } else {
        appContext.tellError(response.msg);
      }
    });
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => {
      appContext.setShowOverlayLoader(false);
    }
  }, [loading]);

  if (ready && data) {
    return (
      <>
        <style>{`
          :root {
            --clinical-teal: #0d9488;
            --navy-900: #0f172a;
            --slate-50: #f8fafc;
            --slate-100: #f1f5f9;
            --slate-200: #e2e8f0;
            --slate-500: #64748b;
            --font-main: 'Inter', system-ui, -apple-system, sans-serif;
          }

          .jd-section {
            padding: clamp(40px, 8vh, 80px) 0;
            background: var(--slate-50);
            min-height: 100vh;
            font-family: var(--font-main);
          }

          .jd-container {
            max-width: 900px;
            margin: 0 auto;
            padding: 0 24px;
          }

          /* Header Styles */
          .jd-header { margin-bottom: 40px; }

          .jd-title {
            color: var(--navy-900);
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 800;
            line-height: 1.1;
            letter-spacing: -0.03em;
            margin-bottom: 16px;
          }

          .jd-status-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 16px;
            border-radius: 30px;
            font-weight: 700;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .status-published { background: #dcfce7; color: #166534; }
          .status-unpublished { background: #fee2e2; color: #991b1b; }
          .status-default { background: var(--slate-200); color: var(--slate-500); }

          /* Card Styles */
          .jd-card {
            background: #ffffff;
            border-radius: 16px;
            border: 1px solid var(--slate-200);
            padding: clamp(24px, 5vw, 40px);
            box-shadow: 0 10px 30px rgba(0,0,0,0.02);
            margin-bottom: 30px;
          }

          .jd-card-title {
            display: flex;
            align-items: center;
            gap: 10px;
            color: var(--navy-900);
            font-weight: 800;
            font-size: 1.25rem;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 1px solid var(--slate-100);
          }

          .jd-content { line-height: 1.8; color: var(--slate-500); font-size: 1.05rem; }
          .jd-content h1, .jd-content h2, .jd-content h3, .jd-content h4 { color: var(--navy-900); font-weight: 700; margin-top: 1.5em; margin-bottom: 0.5em; }
          .jd-content ul { padding-left: 20px; }
          .jd-content li { margin-bottom: 8px; }

          /* Action Bar & Form Styles */
          .jd-actions {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            align-items: center;
            background: #ffffff;
            border-radius: 16px;
            border: 1px solid var(--slate-200);
            padding: 24px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.02);
          }

          .jd-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 14px 28px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.95rem;
            min-height: 48px;
          }

          .jd-btn:disabled { opacity: 0.7; cursor: not-allowed; }
          
          .jd-btn-primary { background: var(--navy-900); color: #ffffff; flex: 1; }
          .jd-btn-primary:hover:not(:disabled) { background: var(--clinical-teal); transform: translateY(-2px); }

          .jd-btn-danger { background: #fee2e2; color: #991b1b; }
          .jd-btn-danger:hover:not(:disabled) { background: #fca5a5; color: #7f1d1d; }

          .jd-btn-success { background: #dcfce7; color: #166534; }
          .jd-btn-success:hover:not(:disabled) { background: #bbf7d0; color: #14532d; }

          /* Input Container */
          .jd-email-prompt {
            display: flex;
            flex-direction: column;
            gap: 16px;
            flex: 1;
            width: 100%;
            align-items: stretch;
            animation: fadeIn 0.3s ease-out;
          }

          .jd-email-input {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid var(--slate-200);
            border-radius: 8px;
            font-family: inherit;
            font-size: 1rem;
            outline: none;
            transition: border-color 0.2s ease;
            box-sizing: border-box;
          }

          .jd-email-input:focus {
            border-color: var(--clinical-teal);
          }

          .jd-btn-group {
            display: flex;
            gap: 12px;
            width: 100%;
          }

          .jd-btn-group .jd-btn {
            flex: 1;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @media (max-width: 768px) {
            .jd-actions { flex-direction: column; align-items: stretch; gap: 12px; }
            .jd-btn { width: 100%; flex: none; }
            .jd-btn-group { flex-direction: column; gap: 10px; }
          }
        `}</style>

        <div className="jd-section">
          <div className="jd-container">
            
            {/* Header Area */}
            <div className="jd-header">
              <h1 className="jd-title">{data.title}</h1>
              <div>
                <span className={`jd-status-badge ${data.status === 'published' ? 'status-published' : data.status === 'unpublished' ? 'status-unpublished' : 'status-default'}`}>
                  {data.status === 'published' ? <FiCheckCircle size={16} /> : <FiClock size={16} />}
                  Status: {data.status}
                </span>
              </div>
            </div>

            {/* Description Body Area */}
            <div className="jd-card">
              <h4 className="jd-card-title">
                <FiFileText color="var(--clinical-teal)" />
                Job Description
              </h4>
              <div 
                className="jd-content"
                dangerouslySetInnerHTML={{ __html: data.description }}
              ></div>
            </div>

            {/* Actions Bar Area */}
            <div className="jd-actions">
              
              {!showEmailPrompt && !showOtpPrompt ? (
                // 1. Default State: Apply Button
                <button
                  className="jd-btn jd-btn-primary"
                  onClick={handleApplyInitiation}
                  disabled={isApplying}
                >
                  {isApplying ? "Checking details..." : "Apply Now"} {!isApplying && <FiArrowRight />}
                </button>
              ) : showOtpPrompt ? (
                // 3. OTP State: Verify Code
                <div className="jd-email-prompt">
                  <div style={{ fontSize: '0.9rem', color: 'var(--clinical-teal)', fontWeight: 600 }}>
                    We sent a code to {emailInput}
                  </div>
                  <input 
                    type="text"
                    className="jd-email-input"
                    placeholder="Enter the 6-digit OTP..."
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                    autoFocus
                  />
                  <div className="jd-btn-group">
                    <button 
                      className="jd-btn jd-btn-primary"
                      onClick={handleOtpSubmit}
                      disabled={isApplying}
                    >
                      {isApplying ? "Verifying..." : "Verify OTP"} {!isApplying && <FiArrowRight />}
                    </button>
                    <button 
                      className="jd-btn jd-btn-danger"
                      onClick={resetPrompts}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // 2. Email State: Request Email
                <div className="jd-email-prompt">
                  <input 
                    type="email"
                    className="jd-email-input"
                    placeholder="Enter your email to proceed..."
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    autoFocus
                  />
                  <div className="jd-btn-group">
                    <button 
                      className="jd-btn jd-btn-primary"
                      onClick={handleEmailSubmit}
                      disabled={isApplying}
                    >
                      {isApplying ? "Processing..." : "Continue"} {!isApplying && <FiArrowRight />}
                    </button>
                    <button 
                      className="jd-btn jd-btn-danger"
                      onClick={resetPrompts}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Publisher Tools */}
              {!showEmailPrompt && !showOtpPrompt && data.status === 'published' && (
                <button
                  className="jd-btn jd-btn-danger"
                  onClick={() => {
                    appContext.activateDialog({
                      message: "Are you sure you want to unpublish this item?",
                      onConfirm: unpublish,
                    })
                  }}
                >
                  <FiEyeOff /> Unpublish
                </button>
              )}

              {!showEmailPrompt && !showOtpPrompt && data.status === 'unpublished' && (
                <button
                  className="jd-btn jd-btn-success"
                  onClick={() => {
                    appContext.activateDialog({
                      message: "Are you sure you want to publish this item?",
                      onConfirm: publish,
                    })
                  }}
                >
                  <FiCheckCircle /> Publish
                </button>
              )}
            </div>

          </div>
        </div>
      </>
    );
  } else {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }
}